import { Injectable, OnDestroy } from '@angular/core';
import { AudioChunk, RecordingState } from '../../../models/stt-chunked.model';
import { BehaviorSubject, Subject } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AudioStreamService } from '../audio-stream/audio-stream.service';
import { VadService } from '../vad/vad.service';

const TARGET_RATE = 16000;
const BITS_PER_SAMPLE = 16;
const CHANNELS = 1;
export const MIN_RECORDING_SECONDS = environment.sttService.audioChunks.minRecordingSeconds;
export const MAX_RECORDING_SECONDS = environment.sttService.audioChunks.maxRecordingSeconds;
export const MIN_CHUNK_SEC = environment.sttService.audioChunks.minChunkInSec;
export const SILENCE_MS = environment.sttService.audioChunks.silenceInMs;
export const OVERLAP_SEC = environment.sttService.audioChunks.overlapSeconds;

const MAX_SILENT_CHUNKS = environment.sttService.audioChunks.maxConsecutiveSilentChunks;
const SOFT_CUT_SEC = environment.sttService.audioChunks.softCutWindowSeconds;
const SOFT_SILENCE_MS = environment.sttService.audioChunks.softCutSilenceInMs;

export const MIN_VOICED_SECONDS = environment.sttService.audioChunks.minVoicedSeconds;

/** Optional per-session override of the chunk window; env values are the fallback. */
export interface ChunkOptions { minRecordingSeconds?: number; maxRecordingSeconds?: number; }

/** isFinal = last audio of a stop/pause/device-loss; lets the upload queue keep it even when full. */
export type FlushedChunk = AudioChunk & { isFinal?: boolean };

@Injectable({
  providedIn: 'root'
})
export class RecordingService implements OnDestroy {
  readonly chunk$ = new Subject<FlushedChunk>();
  readonly deviceDisconnected$ = new Subject<void>();

  private ctx: AudioContext | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private stream: MediaStream | null = null;

  private stateSubject = new BehaviorSubject<RecordingState>('idle');
  readonly state$ = this.stateSubject.asObservable();

  readonly silentChunk$ = new Subject<{ count: number; limitReached: boolean }>();

  // Buffer — pre-allocated concat avoids per-flush allocation
  private frames: Float32Array[] = [];
  private totalSamples = 0;

  private seq = 0;
  private chunkStart = 0;
  private nativeRate = 48000;

  private silentChunks = 0;

  // Resampler state carried across worklet frames (no per-frame sample loss)
  private resamplePhase = 0;
  private resamplePrev = 0;

  // `state` stays 'idle' during the permission prompt, so a 2nd start() would pass the state guard
  private starting = false;

  // active chunk window for this session (override from start(), else env)
  private minSec = MIN_RECORDING_SECONDS;
  private maxSec = MAX_RECORDING_SECONDS;


  activeSessionId: string = '';

  constructor(
    private readonly audioStreamService: AudioStreamService,
    private readonly vad: VadService,
  ) {
  }

  private get state(): RecordingState { return this.stateSubject.value; }
  private set state(v: RecordingState) { this.stateSubject.next(v); }

  async start(sessionId: string, opts?: ChunkOptions): Promise<void> {
    if (this.starting) {
      console.warn('[RecordingService] start() ignored — already starting');
      return;
    }
    if (!['idle', 'stopped'].includes(this.state)) return;
    this.starting = true;

    try {
      this.activeSessionId = sessionId;
      this.minSec = opts?.minRecordingSeconds ?? MIN_RECORDING_SECONDS;
      this.maxSec = Math.max(this.minSec, opts?.maxRecordingSeconds ?? MAX_RECORDING_SECONDS); // max never below min
      console.log(`[RecordingService] chunk window min=${this.minSec}s max=${this.maxSec}s`);

      // HTTPS / secure context check before touching getUserMedia
      if (!window.isSecureContext) {
        throw new Error('Microphone access requires a secure context (HTTPS).');
      }

      // Use shared stream service — restart() force-stops any current stream
      // on this shared singleton before creating a new one. If something
      // else is holding a concurrent stream via the same service, this
      // warning is the only signal you'll get before it gets yanked —
      // confirm no such concurrent consumer exists in this app.
      if (this.audioStreamService.hasStream()) {
        console.warn('[RecordingService] AudioStreamService already had a live stream before start() — it will be stopped and replaced.');
      }

      this.stream = await this.audioStreamService.restart();
      await this.setupCapturePipeline();

      this.reset();
      this.state = 'recording';
    } finally {
      this.starting = false;
    }
  }

  private async setupCapturePipeline(): Promise<void> {
    try {
      // Everything below can throw (AudioContext ctor, addModule 404, AudioWorkletNode ctor
      // failing when the processor never registered, etc). If it does, the
      // stream is already live — teardown() before rethrowing
      // so the caller's catch doesn't leave an orphaned mic stream / leaked
      // AudioContext behind. Without this, a failed setup leaves `state`
      // at 'idle', so the next start() call stacks a second stream on top.
      this.ctx = new AudioContext();
      this.nativeRate = this.ctx.sampleRate;

      // Context created after an await can start 'suspended' (Safari/iOS) → no frames,
      // but the UI would show "recording". Resume, or fail loudly.
      if (this.ctx.state !== 'running') {
        console.warn(`[RecordingService] AudioContext is '${this.ctx.state}' — resuming`);
        await this.ctx.resume();
      }
      // Browser can also suspend it later (phone call, interruption)
      this.ctx.onstatechange = () => this.handleContextStateChange();

      await this.ctx.audioWorklet.addModule('assets/js/pcm-processor.js');
      this.sourceNode = this.ctx.createMediaStreamSource(this.stream!);
      this.workletNode = new AudioWorkletNode(this.ctx, 'pcm-processor');
      this.workletNode.port.onmessage = ({ data }) => this.onFrame(new Float32Array(data));
      this.sourceNode.connect(this.workletNode);
      this.workletNode.connect(this.ctx.destination);
      this.stream!.getAudioTracks().forEach(t => (t.onended = () => this.handleTrackEnded()));
    } catch (e) {
      console.error('[RecordingService] capture pipeline setup failed', e);
      this.teardown();
      this.state = 'idle';
      throw e;
    }
  }

  // Only act while 'recording' — pause() suspends on purpose (state is 'paused' by then).
  private handleContextStateChange(): void {
    // 'interrupted' is Safari-only and missing from the TS type → compare as string
    const s = String(this.ctx?.state);
    console.log(`[RecordingService] AudioContext → ${s}`);
    if (this.state === 'recording' && (s === 'suspended' || s === 'interrupted')) {
      this.ctx!.resume().catch(err => {
        console.error('[RecordingService] could not resume AudioContext — ending recording', err);
        this.handleTrackEnded(); // same recovery as a lost mic
      });
    }
  }

  pause(): void {
    // guard — ctx.suspend() throws if state is not recording
    if (this.state !== 'recording') return;
    this.flush(true);
    this.ctx?.suspend();
    this.state = 'paused';
  }

  async resume(): Promise<void> {
    // guard
    if (this.state !== 'paused') return;
    // awaited so a failure reaches ScribeService.start() instead of an unhandled rejection
    await this.ctx?.resume();
    // reset chunkStart so post-resume chunks have accurate startTime
    this.chunkStart = Date.now();
    this.state = 'recording';
  }

  stop(): void {
    if (this.state === 'idle') return;
    this.flush(true);
    this.teardown();
    // Return to idle so the same service instance can be restarted
    this.state = 'idle';
  }

  // bypasses flush() entirely, no upload for the tail
  discard(): void {
    if (this.state === 'idle') return;
    this.reset();
    this.teardown();
    this.state = 'idle';
  }

  ngOnDestroy(): void {
    if (this.state !== 'idle') {
      this.flush(true);
      this.teardown();
    }
    // signal UploadPipelineService to shut its pipeline
    this.chunk$.complete();
    this.deviceDisconnected$.complete();
    this.stateSubject.complete();
    this.silentChunk$.complete();
  }

  private onFrame(pcm: Float32Array): void {
    if (this.state !== 'recording') return;

    const resampled = this.downsample(pcm, this.nativeRate, TARGET_RATE);
    if (!resampled.length) return;

    this.frames.push(resampled);
    this.totalSamples += resampled.length;

    const now = Date.now();
    this.vad.push(pcm, resampled.length, this.nativeRate, now);

    const durationSec = this.totalSamples / TARGET_RATE;
    const silenceDuration = this.vad.silenceMs(now);
    // Near maxSec accept a shorter pause, so the cut lands between words instead of mid-word
    const softStartSec = this.maxSec - Math.min(SOFT_CUT_SEC, (this.maxSec - this.minSec) / 2);
    const silenceNeededMs = durationSec >= softStartSec ? SOFT_SILENCE_MS : SILENCE_MS;
    const shouldFlushOnSilence = durationSec >= this.minSec && silenceDuration >= silenceNeededMs;
    const shouldForceFlush = durationSec >= this.maxSec;

    if (shouldFlushOnSilence || shouldForceFlush) this.flush(false, !shouldFlushOnSilence);
  }

  private flush(isFinal = false, keepTail = false): void {
    if (!this.frames.length) return;

    const startTime = this.chunkStart;
    const voicedSec = this.vad.voicedSamples / TARGET_RATE;
    const hasSpeech = voicedSec >= MIN_VOICED_SECONDS;
    let tail: Float32Array | null = null;
    let silentEvent: { count: number; limitReached: boolean } | null = null;

    // A short tail WITH speech used to be dropped for being < MIN_CHUNK_SEC.
    // Pad it with silence up to the minimum instead of losing it.
    if (!hasSpeech) console.warn(`[VAD] dropped chunk with no speech (${(this.totalSamples / TARGET_RATE).toFixed(1)}s, voiced ${voicedSec.toFixed(2)}s, floor=${this.vad.noiseFloor.toFixed(4)}, thr=${this.vad.threshold.toFixed(4)})`);

    if (hasSpeech) {
      const merged = this.mergeFrames(this.frames, Math.ceil(MIN_CHUNK_SEC * TARGET_RATE));
      const endTime = Date.now();
      this.chunk$.next({
        seq: this.seq++,
        wav: this.encodeWAV(merged),
        startTime,
        endTime,
        sessionId: this.activeSessionId,
        isFinal,
      });
      // Forced cut only: carry the last OVERLAP_SEC into the next chunk so a cut word is heard whole
      if (keepTail && !isFinal && OVERLAP_SEC > 0) {
        tail = merged.slice(Math.max(0, merged.length - Math.ceil(OVERLAP_SEC * TARGET_RATE)));
        console.log(`[Flush] seq=${this.seq - 1} dur=${(merged.length / TARGET_RATE).toFixed(1)}s final=${isFinal} forced=${keepTail} overlapSec=${OVERLAP_SEC} tail=${(tail.length / TARGET_RATE).toFixed(1)}s`);
      }
    } else if (!isFinal) { // Stop/pause/device-loss flushes don't count
      this.silentChunks++;
      const limitReached = MAX_SILENT_CHUNKS > 0 && this.silentChunks >= MAX_SILENT_CHUNKS;
      silentEvent = { count: this.silentChunks, limitReached };
      if (limitReached) this.silentChunks = 0;
    }

    // Buffer reset BEFORE any event that can re-enter (the component may call stop() → flush(true))
    this.frames = tail ? [tail] : [];
    this.totalSamples = tail ? tail.length : 0;
    this.vad.resetChunk(); // voiced counter + silence timer; the tail itself is not counted as speech
    this.chunkStart = Date.now() - (tail ? (tail.length / TARGET_RATE) * 1000 : 0);

    if (silentEvent) this.silentChunk$.next(silentEvent);
  }

  /** Called per transcribed chunk. Text = real speech, streak over; blank = counts like a no-speech chunk. */
  noteTranscribed(hasText: boolean): void {
    if (this.state !== 'recording') return; // late responses after Stop/pause don't count
    if (hasText) { this.silentChunks = 0; return; }
    const count = ++this.silentChunks;
    const limitReached = MAX_SILENT_CHUNKS > 0 && count >= MAX_SILENT_CHUNKS;
    if (limitReached) this.silentChunks = 0;
    this.silentChunk$.next({ count, limitReached });
  }

  private reset(): void {
    this.frames = [];
    this.totalSamples = 0;
    this.seq = 0;
    this.chunkStart = Date.now();
    this.resamplePhase = 0;
    this.resamplePrev = 0;
    this.silentChunks = 0;
    this.vad.reset();
  }

  private handleTrackEnded(): void {
    if (!this.stream) return;
    console.warn('[RecordingService] input track ended — flushing and stopping');
    this.flush(true);
    this.teardown();
    this.state = 'stopped';
    // re-exposed by ScribeService; the component resets its UI on it
    this.deviceDisconnected$.next();
  }

  // teardown order — worklet → source → context. Stream stop delegated to
  // AudioStreamService (single owner of the shared MediaStream lifecycle).
  private teardown(): void {
    this.stream?.getAudioTracks().forEach(t => (t.onended = null));
    this.audioStreamService.stop();
    this.stream = null;

    if (this.workletNode) {
      this.workletNode.port.onmessage = null;
      this.workletNode.disconnect();
      this.workletNode = null;
    }

    this.sourceNode?.disconnect();
    this.sourceNode = null;

    if (this.ctx) {
      this.ctx.onstatechange = null;
      this.ctx.close().catch(e => console.warn('[RecordingService] ctx.close() failed', e));
      this.ctx = null;
    }
  }

  // Streaming linear resampler: the fractional read position and last sample are
  // kept between frames, so nothing is dropped/discontinuous at frame boundaries
  // (the old per-frame version lost ~1.5% of samples at 48k→16k).
  private downsample(
    buf: Float32Array,
    fromRate: number,
    toRate: number,
  ): Float32Array {
    if (fromRate === toRate) return buf;
    const n = buf.length;
    if (!n) return buf;
    const ratio = fromRate / toRate;
    // pos is in this frame's index space; -1 means "last sample of previous frame"
    let pos = this.resamplePhase;
    const out = new Float32Array(Math.ceil((n - pos) / ratio) + 1);
    let count = 0;
    while (pos < n - 1) {
      const i0 = Math.floor(pos);
      const s0 = i0 < 0 ? this.resamplePrev : buf[i0];
      const s1 = buf[i0 + 1];
      out[count++] = s0 + (s1 - s0) * (pos - i0);
      pos += ratio;
    }
    this.resamplePhase = pos - n;
    this.resamplePrev = buf[n - 1];
    return out.subarray(0, count);
  }

  // zero-pads (silence) up to minLen samples if shorter
  private mergeFrames(frames: Float32Array[], minLen = 0): Float32Array {
    const total = frames.reduce((s, f) => s + f.length, 0);
    const out = new Float32Array(Math.max(total, minLen));
    let offset = 0;
    for (const f of frames) {
      out.set(f, offset);
      offset += f.length;
    }
    return out;
  }

  private encodeWAV(pcm: Float32Array): Blob {
    const bytesPerSample = BITS_PER_SAMPLE / 8;
    const blockAlign = CHANNELS * bytesPerSample;
    const byteRate = TARGET_RATE * blockAlign;

    const pcm16 = new Int16Array(pcm.length);
    for (let i = 0; i < pcm.length; i++) {
      const s = Math.max(-1, Math.min(1, pcm[i]));
      pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }

    const dataLen = pcm16.byteLength;
    const buf = new ArrayBuffer(44 + dataLen);
    const v = new DataView(buf);

    const u32 = (o: number, val: number) => v.setUint32(o, val, true);
    const u16 = (o: number, val: number) => v.setUint16(o, val, true);
    const str = (o: number, s: string) =>
      [...s].forEach((c, i) => v.setUint8(o + i, c.charCodeAt(0)));

    str(0, 'RIFF');
    u32(4, 36 + dataLen);
    str(8, 'WAVE');
    str(12, 'fmt ');
    u32(16, 16);
    u16(20, 1);
    u16(22, CHANNELS);
    u32(24, TARGET_RATE);
    u32(28, byteRate);
    u16(32, blockAlign);
    u16(34, BITS_PER_SAMPLE);
    str(36, 'data');
    u32(40, dataLen);
    new Int16Array(buf, 44).set(pcm16);

    return new Blob([buf], { type: 'audio/wav' });
  }
}